import React, { useState, useEffect } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useOrganization } from "hooks/OrganizationContext";
import { getProfileSolutions } from "api/solutions";

const mapStyles = [
  {
    elementType: "geometry",
    stylers: [
      {
        color: "#242f3e"
      }
    ]
  },
  {
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#242f3e"
      }
    ]
  },
  {
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#F1F5F9"
      }
    ]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [
      {
        color: "#4A596B"
      }
    ]
  },
  
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        color: "#1A1A1A"
      }
    ]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#515c6d"
      }
    ]
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#17263c"
      }
    ]
  }
];

interface AddProjectGoogleMapProps {
  height: string;
  initialCoordinates?: [number | null, number | null];
  onCoordinatesChange?: (coords: [number | null, number | null]) => void;
}

const AddProjectGoogleMap: React.FC<AddProjectGoogleMapProps> = ({ 
  height, 
  initialCoordinates = [null, null],
  onCoordinatesChange 
}) => {
    // Independent state, not using the shared context
    const [localCoordinates, setLocalCoordinates] = useState<[number | null, number | null]>(initialCoordinates);
    const [refreshMode, setRefreshMode] = useState('');
    const [loading, setLoading] = useState(true);
    const [menuShown, setMenuShown] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<any>(null);
    const [userProjects, setUserProjects] = useState<any>([]);
    const { selectedOrganization } = useOrganization();
    const [center, setCenter] = useState({
      lat: 24,
      lng: 42.388945,
    });
    const [markersLoaded, setMarkersLoaded] = useState(false);
  
    // Update local state when props change
    useEffect(() => {
      if (initialCoordinates && initialCoordinates[0] !== null && initialCoordinates[1] !== null) {
        setLocalCoordinates(initialCoordinates);
      }
    }, [initialCoordinates]);
  
    useEffect(() => {
      if (localCoordinates[0] !== null && localCoordinates[0] !== 0 && 
          localCoordinates[1] !== null && localCoordinates[1] !== 0) {
        setCenter({ lat: localCoordinates[0], lng: localCoordinates[1] });
      }
    }, [localCoordinates]);
  
    const getAllProjects = async (id: string) => {
      try {
        setLoading(true);
        const response = await getProfileSolutions(id);
        if (response.status === 200) {
          const newProjects = response.data.list
            .map((item: any) => ({
              ...item,
              title: item.name,
              type: "PROJECT",
              bgColor: "bg-gradient-to-b from-green to-blue",
              icon: "/images/location-pin.png",
              status: "Private Project",
              link: item.url,
              isEditable: true,
              coordinates: item.coordinates ? JSON.parse(item.coordinates) : null
            }))
            .filter((project: any) => project.coordinates !== null);
          
          setUserProjects(newProjects);
        }
      } catch (error) {
        console.error("Error loading projects:", error);
      } finally {
        setLoading(false);
        setMarkersLoaded(true);
      }
    };
  
    useEffect(() => {
      getAllProjects(selectedOrganization.id);
    }, [selectedOrganization, refreshMode]);
  
    const containerStyle = {
      width: "100%",
      height: height,
    };
    
    const { isLoaded } = useJsApiLoader({
      id: "google-map-script",
      googleMapsApiKey: "AIzaSyCaAnlPllExl2Yuk5G6RENFb0zlBQUXFWY",
    });
    
    useEffect(() => {
      if (isLoaded) {
        setLoading(false);
      }
    }, [isLoaded, userProjects]);
    
    const [map, setMap] = React.useState(null);
    
    const onLoad = () => {
      setMenuShown(true);
    };
    
    const onMapClick = (event: any) => {
      // Capture the latitude and longitude from the event
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setSelectedLocation({ lat, lng });
      
      // Update local coordinates
      const newCoords: [number, number] = [Number(lat), Number(lng)];
      setLocalCoordinates(newCoords);
      
      // Notify parent component if callback provided
      if (onCoordinatesChange) {
        onCoordinatesChange(newCoords);
      }
    };
  
  return (
    <div>
      <GoogleMap
        mapContainerStyle={{ ...containerStyle, position: 'relative', zIndex: 1 }}
        center={center}
        zoom={5.5}
        onLoad={onLoad}
        onClick={onMapClick}
        options={{
          styles: mapStyles,
          disableDefaultUI: false,
          backgroundColor: '#1A1A1A',
          zoomControl: true,
          mapTypeControl: false,
          fullscreenControl: false,
          minZoom: 3,
        }}
      >
        {/* Show the marker for the selected coordinates */}
        {localCoordinates[0] !== null && localCoordinates[1] !== null && 
          <Marker
            icon={{
              url: "https://www.iconpacks.net/icons/2/free-location-pin-icon-2965-thumb.png",
              scaledSize: new window.google.maps.Size(28, 28),
            }}
            position={{ lat: localCoordinates[0], lng: localCoordinates[1] }}
            animation={window.google.maps.Animation.DROP}
          />
        }
        
      
      </GoogleMap>
    </div>
  );
};

export default AddProjectGoogleMap;
